// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import type { ClinicalReviewListQuery, ClinicalReviewRow } from '../../api/client'
import type { ClinicalCounts } from '../../api/carerxClinical'
import { funnelToServerOps, type ColumnFilters } from '../../components/common/ColumnFilter'

// CareRx wave-1 #2 / #3: the Clinical Review page's tiles, quick filters and server sort, as pure
// functions so the tile → list mapping is testable. A tile's number is the length of the list its
// click shows: the queue tiles count crViewRows over the queue; Conditional and Denied leave the
// queue (a conditional determination opens a screening hold, a denial cancels the therapy), so they
// are counted by the counts endpoint with the SAME predicate their list endpoint runs.

export type CrQuick = 'pending' | 'draft' | 'in_progress' | 'pending_callback' | 'cancelled' | 'all' | 'approved' | 'denied' | 'conditional'
export type CrArchive = 'decided' | 'conditional'
export type CrTileKey = 'in_review' | 'pending' | 'in_progress' | 'unassigned' | 'needs_labs' | 'callback' | 'conditional' | 'denied'

export interface CrTileDef {
  key: CrTileKey; label: string; quick: CrQuick; tone?: 'warn' | 'bad'
  /** Narrows the quick filter's rows further (queue tiles that are not a status). */
  match?: (r: ClinicalReviewRow) => boolean
}

export const CR_TILES: CrTileDef[] = [
  { key: 'in_review', label: 'In review', quick: 'all' },
  { key: 'pending', label: 'Pending', quick: 'pending', tone: 'warn' },
  { key: 'in_progress', label: 'In progress', quick: 'in_progress' },
  { key: 'unassigned', label: 'Unassigned', quick: 'all', tone: 'warn', match: r => !r.assigned_rph_id && r.cr_status !== 'cancelled' },
  { key: 'needs_labs', label: 'Needs labs', quick: 'all', tone: 'bad', match: r => r.labs_flag === 'missing' && r.cr_status !== 'cancelled' },
  { key: 'callback', label: 'Pending call back', quick: 'pending_callback', tone: 'warn' },
  { key: 'conditional', label: 'Conditional', quick: 'conditional', tone: 'bad' },
  { key: 'denied', label: 'Denied', quick: 'denied', tone: 'bad' },
]

/** The read-only list a quick filter reads from; null = the working queue. */
export const archiveOf = (q: CrQuick): CrArchive | null =>
  q === 'approved' || q === 'denied' ? 'decided' : q === 'conditional' ? 'conditional' : null

/** The rows a quick filter (+ tile) shows, before the header funnels. */
export function crViewRows(quick: CrQuick, tile: CrTileKey | null, queue: ClinicalReviewRow[],
  archive: Record<CrArchive, ClinicalReviewRow[] | null>): ClinicalReviewRow[] {
  const a = archiveOf(quick)
  if (a === 'decided') return (archive.decided ?? []).filter(x => x.determination === quick)
  if (a === 'conditional') return archive.conditional ?? []
  const rows = quick === 'all' ? queue : queue.filter(x => x.cr_status === quick)
  const match = tile ? CR_TILES.find(t => t.key === tile)?.match : undefined
  return match ? rows.filter(match) : rows
}

/** A tile's number, or undefined while its source has not answered (the tile reads "—"). */
export function crTileCount(def: CrTileDef, queue: ClinicalReviewRow[], counts: ClinicalCounts | undefined): number | undefined {
  if (def.key === 'conditional') return counts?.conditional
  if (def.key === 'denied') return counts?.denied
  return crViewRows(def.quick, def.key, queue, { decided: null, conditional: null }).length
}

/** Which tile the current selection IS — a status chip click lights its tile too. */
export function activeCrTile(quick: CrQuick, tile: CrTileKey | null): CrTileKey | null {
  if (tile) return tile
  return CR_TILES.find(t => !t.match && t.quick === quick)?.key ?? null
}

/** Clicking a tile selects its set; clicking the active tile clears to everything in the queue. */
export function clickCrTile(active: CrTileKey | null, clicked: CrTileKey): { quick: CrQuick; tile: CrTileKey | null } {
  if (active === clicked) return { quick: 'all', tile: null }
  const def = CR_TILES.find(t => t.key === clicked)!
  return { quick: def.quick, tile: def.match ? def.key : null }
}

// ── #3: server sort + date ranges ───────────────────────────────────────────────────────────────
export type CrSortKey = 'next_infusion' | 'deliver_on'
export interface CrSort { key: CrSortKey; dir: 'asc' | 'desc' }

/** Header click cycle: unsorted → ascending → descending → unsorted (the default order). */
export function nextCrSort(cur: CrSort | null, key: CrSortKey): CrSort | null {
  if (!cur || cur.key !== key) return { key, dir: 'asc' }
  return cur.dir === 'asc' ? { key, dir: 'desc' } : null
}

const RANGES = [
  ['next_infusion', 'next_infusion_from', 'next_infusion_to'],
  ['deliver_on', 'deliver_on_from', 'deliver_on_to'],
] as const

/** The list request for a sort + the header funnels: the two date funnels go to the server, so a
 *  row beyond the 1,000-row cap is never missing from a ranged or sorted view. */
export function crListQuery(sort: CrSort | null, filters: ColumnFilters): ClinicalReviewListQuery {
  const q: ClinicalReviewListQuery = {}
  if (sort) { q.sort = sort.key; q.dir = sort.dir }
  for (const [col, from, to] of RANGES) {
    for (const p of funnelToServerOps(filters[col], ['gte', 'lte']).preds) {
      if (p.op === 'gte') q[from] = p.values[0]
      if (p.op === 'lte') q[to] = p.values[0]
    }
  }
  return q
}
